import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Helpers } from '../core/src/helpers';
import { fetchPrices, NO_PRICE } from '../product/product-actions';
import { fetchDeals } from '../product/components/deals/deals-actions';
import { isDealActive } from '../product/components/deals';

const selectPrices = Helpers.memoize(
    (prices, products, ctoConfigurations, user, deals) => {
        return {
            prices: (products || []).reduce((r, p) => {
                if (!p) {
                    return r;
                }
                let priceObj = { ...Helpers.getProductPrice(p, prices) };
                let prodDeals = deals[p.itemId || p.catentryId];
                const ctoConfig = (ctoConfigurations || {})[p.sku];
                const { items, productType } = priceObj;
                if (user && user.TierPriceMsg && priceObj.tierPrice) {
                    priceObj.tierMsg = user.TierPriceMsg;
                }
                if (isDealActive(prodDeals)) {
                    const deal = prodDeals.activeDeal;
                    priceObj.salePrice = deal.dealPrice * 1;
                    priceObj.deal = deal;
                }
                //if product is a kit attach kit item prices (AS OF NOW HPSERVICES RETURNS UNDERLYING KIT ITEM PRICES WHEN YOU FETCH THE KIT PRICE)
                if (productType !== 'GIFTCARD' && items && Object.keys(items).length > 0) {
                    priceObj.items = Object.keys(items).reduce((r, key) => {
                        let item = items[key];
                        let itemPriceObj = Helpers.getProductPrice(item, prices);
                        r[key] = { ...itemPriceObj, ...item };
                        return r;
                    }, {});
                }
                //if ctoConfig is available for product and a pre config has been selected with price, use that price
                if (ctoConfig && ctoConfig.price) {
                    priceObj = { ...priceObj, ...ctoConfig.price };
                }
                r[p.sku] = priceObj;
                return r;
            }, {}),
        };
    },
    (prices, products, ctoConfigurations, user, deals) => {
        const cacheKey = (products || []).reduce((r, p) => {
            if (!p) {
                return '';
            }
            const itemId = p.itemId || p.catentryId;
            return r.concat(
                `${p.sku}-${p.sku in prices}-${itemId in deals}-${(user || {}).TierPriceMsg}-${
                    ctoConfigurations && (ctoConfigurations[p.sku] || {}).configCatentryId
                }`
            );
        }, ``);
        return cacheKey;
    }
);

/**
 * @typedef {Object} Product
 * @property {string} sku - product SKU
 * @property {string} itemId - legacy id of product used to fetch price
 * @property {string} catentryId - new id for fetching product price
 * @property {Array.<Product>} bundleComponents - array of bundle products that also need prices
 */

/**
 * @name usePricePrice
 * @param {Array.<Product>} products
 * @returns {object} - Product price hash map by sku
 */
export default function useProductPrice(products = [], options = {}) {
    const dispatch = useDispatch();
    const prices = useSelector(state => state.productData.productInfo.productPrices);
    const user = useSelector(state => state.userData);
    const deals = useSelector(state => state.deals);
    const ctoConfigurations = useSelector(state => {
        const { withCTOConfigurations } = options;
        if (withCTOConfigurations) {
            return state.productData.ctoConfigurations;
        }
        return null;
    });
    useEffect(() => {
        let noPrice = (products || []).filter(product => {
            if (!product) {
                return false;
            }
            const { sku, itemId, catentryId, prdClass, product_class } = product;
            const priceFailure = prices[itemId || catentryId];
            const productType = prdClass || product_class;
            //when prices fai to fetch, we get no response back so we dont know the sku
            // which mean we need to check for fetch fail by itemId
            return !(productType in NO_PRICE) && !(sku in prices) && (!priceFailure || !priceFailure.priceFetchFailed);
        });
        if (noPrice.length > 0) {
            dispatch(
                fetchPrices(
                    noPrice.map(p => p.itemId || p.catentryId),
                    true
                )
            );
        }
        //check for deals if we haven't already
        if (!deals || !deals.dealsFetched) {
            dispatch(fetchDeals());
        }
    }, [products]);
    return selectPrices(prices, products, ctoConfigurations, user, deals);
}
